import { getCloudinary } from "shared/cloudinary";
import { ResizeSimpleAction } from "@cloudinary/transformation-builder-sdk/actions/resize/ResizeSimpleAction";
import type { AssetImage } from "gql/generated.ts";
import { styled, Box, Flex } from "styled-system/jsx";
import { JsxStyleProps } from "styled-system/types";
import { getMultilingualString } from "shared/utils/getMultilingualString.ts";
import { Icon, Spinner, Text } from "@ttc3k/trekker";
import { MediaImage, MediaImageXmark } from "iconoir-react";
import {
  CSSProperties,
  ForwardRefExoticComponent,
  RefAttributes,
  SVGProps,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { Skeleton, SkeletonProps } from "../Skeleton";
import { Resize } from "@cloudinary/transformation-builder-sdk/actions";
import { Blurhash } from "react-blurhash";
import { getImageThumbnailBackgroundColor } from "shared/utils/getImageBackgroundColor";
import { usePostHog } from "shared/hooks";
import { PostHogErrorBoundary } from "posthog-js/react";
import {
  MUX_IMAGE,
  VideoPlayer,
} from "shared/components/molecules/VideoPlayer/VideoPlayer";
import { MutedMuxPreview } from "shared/components/molecules/MutedMuxPreview";

const BACKOFF_DELAY = 2000;
const MAX_RETRIES = 8;

/** Error message for missing image - used so error boundary can show placeholder instead of reporting */
const NO_IMAGE_MESSAGE = "No Image";

type ImageNotFoundProps = {
  Icon: ForwardRefExoticComponent<
    Omit<SVGProps<SVGSVGElement>, "ref"> & RefAttributes<SVGSVGElement>
  >;
  imageStyleProps?: JsxStyleProps & { className?: string };
  alt?: string;
  errorBoundaryProps?: SkeletonProps & { className?: string };
  parts?: CloudinaryImageProps["parts"];
};

/**
 * When an image isn't found or Cloudinary throws an error,
 * we'll fall back to this image placeholder
 * - Use the Skeleton with the MediaImage icon in the center
 */
const ImageNotFound = ({
  Icon: IconElement,
  imageStyleProps,
  alt,
  errorBoundaryProps,
  parts,
}: ImageNotFoundProps) => {
  const { t } = useTranslation();

  // Remove image hover effects directly assigned
  const strippedStyleProps = imageStyleProps?._hover
    ? { ...imageStyleProps, _hover: {} }
    : imageStyleProps;

  // Remove image hover effects assigned via className
  if (strippedStyleProps?.className) {
    strippedStyleProps.className = strippedStyleProps.className
      .replace(/\bhover:[\w.-]+/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  const caption = alt ? alt : t("core:IMAGE.NOT_FOUND");

  return (
    <Skeleton
      width="auto"
      height="full"
      {...strippedStyleProps}
      justifyItems={"center"}
      alignContent={"center"}
      p="0"
      {...errorBoundaryProps}
      title={caption}
      minH="0px"
      part={parts?.container}
    >
      <Icon
        Element={IconElement}
        size="xl"
        alt={caption}
        color={"icon.light"}
        part={parts?.image}
      />
    </Skeleton>
  );
};

export interface CloudinaryImageProps {
  // Image is possibly undefined or null as we will display a placeholder image
  // in those cases
  image?: Partial<AssetImage> | null;
  imageStyleProps?: JsxStyleProps & { className?: string };
  resize?: ResizeSimpleAction;
  resizeVideo?: ResizeSimpleAction;
  previewVideo?: boolean;
  alt?: string;
  blurhash?: string;
  errorBoundaryProps?: SkeletonProps & { className?: string };
  includeBgBlurHash?: boolean;
  /**
   * Optional solid background color to use instead of blurhash
   * When provided, this will be used as the background color for the placeholder
   * instead of displaying the blurhash
   */
  solidBackgroundColor?: "#000000" | null;
  style?: CSSProperties;
  parts?: { image?: string; container?: string };
  /**
   * Video specific props
   */
  videoSpecificProps?: {
    showControls?: boolean;
    autoPlay?: boolean;
    loop?: boolean;
    muted?: boolean;
    playsInline?: boolean;
  };
}

/**
 * This is the "real" component. It needs to be wrapped by the ErrorBoundary,
 * so is this is not exported externally
 * @param param0
 * @returns
 */
const CloudinaryImageInner = ({
  image,
  imageStyleProps,
  resize,
  resizeVideo,
  previewVideo,
  alt,
  blurhash,
  errorBoundaryProps,
  includeBgBlurHash,
  solidBackgroundColor,
  style,
  parts,
  videoSpecificProps,
}: CloudinaryImageProps) => {
  const { t } = useTranslation();

  const videoRef = useRef<HTMLVideoElement>(null);
  const [imageNotFound, setImageNotFound] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isMuxHovering, setIsMuxHovering] = useState(false);

  if (!image || !image._id) {
    throw new Error(NO_IMAGE_MESSAGE);
  }

  const isVideo = image.isVideo;
  const muxPlaybackId = image.mux?.playbackId ?? undefined;
  const muxStatus = image.mux?.status;
  const isMuxPreparing =
    Boolean(image.mux?.assetId) &&
    (muxStatus === "preparing" || (!muxPlaybackId && muxStatus !== "ready"));
  const isMuxReady = Boolean(muxPlaybackId) && muxStatus === "ready";
  const usesMux = isMuxPreparing || isMuxReady;
  const imageBlurhash = image.blurhash ?? blurhash ?? "";
  const caption = alt
    ? alt
    : (getMultilingualString(image.caption ?? {}) ??
      t("core:IMAGE.NO_CAPTION"));

  const cld = getCloudinary(image._id);
  const cldImage = isVideo
    ? cld.video(image._id).addTransformation(previewVideo ? "e_preview" : "")
    : cld.image(image._id).addTransformation("f_auto");
  /**
   * Take the device's pixel ratio into account
   * e.g. Retina displays have a pixel density of 2,
   *      so we can load in a larger image so that it
   *      looks better when displayed
   */
  const cldResize =
    resize && !isVideo
      ? resize.addQualifier(`dpr_${window.devicePixelRatio}`)
      : resizeVideo && isVideo
        ? resizeVideo.addQualifier(`dpr_${window.devicePixelRatio}`)
        : Resize.scale().addQualifier(`dpr_${window.devicePixelRatio}`);
  cldImage.resize(cldResize);

  /**
   * Loads the video into the video element
   * - When assets in Cloudinary are being processed, the asset is locked
   * - If a call to that asset occurs while it is locked, Cloudinary will return a 423 status code
   * - We'll call the HEAD request in a loop (up to MAX_RETRIES) until the asset is ready
   * - We'll use a backoff delay of BACKOFF_DELAY * 2^attempt
   */
  const loadVideo = useCallback(async (url: string, attempt: number = 0) => {
    try {
      // Use fetch to check if video is ready
      const response = await fetch(url, { method: "HEAD" }); // HEAD is faster than GET
      if (response.status === 423) {
        throw new Error("Locked");
      }
      if (!response.ok) {
        throw new Error(`HTTP error ${response.status}`);
      }

      // If ready, set video source
      videoRef.current!.src = url;
      videoRef.current!.load();
      setImageLoaded(true);
    } catch (err) {
      if (
        err instanceof Error &&
        err.message === "Locked" &&
        attempt < MAX_RETRIES
      ) {
        const delay = BACKOFF_DELAY * Math.pow(2, attempt);
        console.warn(`Preview locked, retrying in ${delay}ms...`);
        attempt++;
        setTimeout(() => loadVideo(url, attempt), delay);
      } else {
        setImageNotFound(false);
      }
    }
  }, []);

  /**
   * Load the image into memory
   * - While the image is loading, the blurhash will be displayed
   * - If loading fails, display <ImageNotFound />
   */
  const imageUrl = cldImage.toURL();
  useEffect(() => {
    // Mux assets skip Cloudinary loading entirely.
    if (usesMux) return;

    // For videos, we'll do a Cloudinary HEAD request to ensure that the asset is available
    // and, if successful, set the src of the video element
    setImageLoaded(false);
    setImageNotFound(false);

    if (isVideo) {
      setTimeout(() => loadVideo(imageUrl, 1), 0);
      return;
    }

    const img = new Image();
    img.onload = () => setImageLoaded(true);
    img.onerror = () => setImageNotFound(true);
    if (imageUrl) {
      img.src = imageUrl;
    }
  }, [imageUrl, isVideo, loadVideo, usesMux]);

  // Mux preparing: processing placeholder (no Cloudinary / HLS yet)
  if (isMuxPreparing) {
    return (
      <Box
        part={parts?.container}
        position="relative"
        overflow="hidden"
        {...imageStyleProps}
        style={style}
      >
        {imageBlurhash ? (
          <Blurhash
            hash={imageBlurhash}
            width="100%"
            height="100%"
            resolutionX={32}
            resolutionY={32}
            punch={1}
          />
        ) : (
          <Box bg="bg.lighter" position="absolute" inset="0" />
        )}
        <Flex
          flexDir="column"
          justifyContent="center"
          alignItems="center"
          gap="50"
          position="absolute"
          inset="0"
          bg="rgba(0,0,0,0.35)"
        >
          <Spinner w="20px" h="20px" />
          <Text color="text.lightest" visual="smallSemiBold" textAlign="center" px="100">
            {t("core:MEDIA.PROCESSING_VIDEO")}
          </Text>
        </Flex>
      </Box>
    );
  }

  // Mux ready: HLS player when controls requested; otherwise Mux thumbnail
  if (isMuxReady && muxPlaybackId) {
    const showControls = videoSpecificProps?.showControls ?? false;
    if (showControls) {
      return (
        <Box part={parts?.container} position="relative" style={style}>
          <VideoPlayer playbackId={muxPlaybackId} />
        </Box>
      );
    }

    if (imageNotFound) {
      return (
        <ImageNotFound
          Icon={MediaImageXmark}
          imageStyleProps={imageStyleProps}
          alt={t("core:IMAGE.NOT_FOUND")}
          errorBoundaryProps={errorBoundaryProps}
        />
      );
    }

    const thumbUrl = `${MUX_IMAGE}/${muxPlaybackId}/thumbnail.jpg`;
    // Size constraints live on the outer box so hover playback cannot expand layout.
    return (
      <Box
        part={parts?.container}
        position="relative"
        overflow="hidden"
        display="inline-block"
        maxW="100%"
        {...imageStyleProps}
        style={style}
        onMouseEnter={() => setIsMuxHovering(true)}
        onMouseLeave={() => setIsMuxHovering(false)}
      >
        <styled.img
          src={thumbUrl}
          alt={caption}
          part={parts?.image}
          display="block"
          w="100%"
          h="100%"
          maxW="100%"
          maxH="100%"
          objectFit="cover"
          style={{
            opacity: isMuxHovering ? 0 : 1,
            transition: "opacity 120ms ease",
          }}
          onError={() => setImageNotFound(true)}
        />
        {isMuxHovering && (
          <MutedMuxPreview playbackId={muxPlaybackId} active />
        )}
      </Box>
    );
  }

  // When loading with new Image or styled.img, the "onerror" event doesn't trigger the ErrorBoundary
  // when throwing a new Error. So, instead, we call setImageNotFound(false) to show
  // the fallback image state
  if (imageNotFound) {
    return (
      <ImageNotFound
        Icon={MediaImageXmark}
        imageStyleProps={imageStyleProps}
        alt={t("core:IMAGE.NOT_FOUND")}
        errorBoundaryProps={errorBoundaryProps}
      />
    );
  }

  /**
   * Try to determine the size for the blurhash image
   * 1. Check the Cloudinary resize options and if not found
   * 2. Check the imageStyleProps
   */
  let blurWidth =
    cldResize.qualifiers.get("w")?.qualifierValue.values[0] ??
    imageStyleProps?.w ??
    imageStyleProps?.width ??
    imageStyleProps?.maxW ??
    imageStyleProps?.maxWidth ??
    imageStyleProps?.minW ??
    imageStyleProps?.minWidth;

  let blurHeight =
    cldResize.qualifiers.get("h")?.qualifierValue.values[0] ??
    imageStyleProps?.h ??
    imageStyleProps?.height ??
    imageStyleProps?.maxH ??
    imageStyleProps?.maxHeight ??
    imageStyleProps?.minH ??
    imageStyleProps?.minHeight;

  // Make sure the values are strings and end in px or %
  blurWidth = (
    typeof blurWidth === "number" ? blurWidth.toString() : (blurWidth ?? "auto")
  ).replace(/(\d)$/, "$1px");

  blurHeight = (
    typeof blurHeight === "number"
      ? blurHeight.toString()
      : (blurHeight ?? "auto")
  ).replace(/(\d)$/, "$1px");

  // NOTE: we intentionally do NOT apply the blurhash as a backgroundImage on
  // the <img>. Even with object-fit:cover, subpixel gaps between the image
  // content edge and the element boundary let the background bleed through,
  // creating a visible colored border artifact. The <Blurhash> component
  // above already handles the loading placeholder.

  const {
    showControls = false,
    autoPlay = true,
    loop = true,
    muted = true,
    playsInline = true,
  } = videoSpecificProps ?? {};

  return (
    <Box part={parts?.container} position="relative">
      <styled.div
        style={{
          display: imageLoaded ? "none" : "block",
          backgroundColor:
            !imageLoaded && solidBackgroundColor
              ? solidBackgroundColor
              : undefined,
        }}
        aspectRatio={imageStyleProps!.aspectRatio}
        position="relative"
        overflow="hidden"
        maxWidth={blurWidth}
        maxHeight={blurHeight}
        className={imageStyleProps!.className}
        bg={
          imageLoaded
            ? "transparent"
            : solidBackgroundColor
              ? undefined
              : "bg.lighter"
        }
      >
        {imageBlurhash &&
          imageBlurhash.length > 0 &&
          includeBgBlurHash &&
          !solidBackgroundColor && (
            <Blurhash
              hash={imageBlurhash}
              width={blurWidth}
              height={blurHeight}
              resolutionX={32}
              resolutionY={32}
              punch={1}
            />
          )}
        {!imageLoaded && isVideo && (
          <Flex
            flexDir="column"
            justifyContent="center"
            alignItems="center"
            gap="50"
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            w="100%"
          >
            <Spinner w="16px" h="16px" />
            <styled.span color="text.mid" fontSize="9px" textAlign="center">
              {t("core:LOADING")}...
            </styled.span>
          </Flex>
        )}
      </styled.div>
      {!isVideo ? (
        <styled.img
          src={imageUrl}
          alt={caption}
          {...imageStyleProps}
          display={imageLoaded ? "inline" : "none"}
          onError={() => setImageNotFound(true)}
          style={style}
          part={parts?.image}
        />
      ) : (
        <styled.video
          ref={videoRef}
          title={caption}
          autoPlay={autoPlay}
          loop={loop}
          muted={muted}
          playsInline={playsInline}
          controls={showControls}
          {...imageStyleProps}
          display={imageLoaded ? "inline" : "none"}
          style={{
            ...style,
            objectFit: "contain", // Override the default to always use contain for videos
          }}
          part={parts?.image}
          onError={() => setImageNotFound(true)}
        />
      )}
    </Box>
  );
};

/**
 * The CloudinaryImage serves several purposes
 * - It loads the image, displaying a blurhash while loading
 * - It takes the user's devicePixelRatio into account when requesting the image size
 * - It replaces the blurhash with the actual image after it loads
 *
 * The CloudinaryImageInner is wrapped in an ErrorBoundary so that the error
 * does flow up to the main ErrorBoundary in RootLayout. Errors like NO_IMAGE_MESSAGE
 * are not reported to PostHog (see additionalProperties.doNotSendToPostHog).
 *
 * Errors that occur loading the image are trapped internally by the onerror handler of Image/img,
 * so to handle those, we set a state in CloudinaryImageInner and show the empty state with a
 * broken image placeholder.
 *
 * NOTE:
 * The default aspectRatio is "square -> 1 / 1" as most images on the site are square or very close
 * to it. If you need a different aspectRatio (e.g. OperatorWidgetCard), you can pass in "auto".
 * - To calcuate image aspect ratios from the Figma images, use https://andrew.hedges.name/experiments/aspect_ratio/
 */
export const CloudinaryImage = ({
  image,
  imageStyleProps,
  resize,
  resizeVideo,
  previewVideo,
  alt,
  blurhash,
  errorBoundaryProps = {
    size: "default",
    mode: "lightest",
    visual: "primary",
  },
  includeBgBlurHash = false,
  solidBackgroundColor,
  style,
  parts,
  videoSpecificProps,
}: CloudinaryImageProps) => {
  const { t } = useTranslation();
  const { getPostHogErrorCaptureData } = usePostHog();

  // Add some default styling to avoid stretching
  if (!imageStyleProps) {
    imageStyleProps = {} as JsxStyleProps & { className?: string };
  }
  // If the image aspect ratio is in the className, move it from there into the imageStyleProps
  if (
    imageStyleProps.className &&
    imageStyleProps.className.indexOf("aspect") > -1
  ) {
    const aspectRatio = imageStyleProps.className?.substring(
      imageStyleProps.className.indexOf("aspect") + 7,
      imageStyleProps.className.indexOf(
        " ",
        imageStyleProps.className.indexOf("aspect"),
      ),
    );
    imageStyleProps.aspectRatio = aspectRatio;
    imageStyleProps.className = imageStyleProps.className.replace(
      `aspect_${aspectRatio}`,
      "",
    );
  }

  imageStyleProps.aspectRatio = imageStyleProps.aspectRatio ?? "square";
  imageStyleProps.objectFit = imageStyleProps.objectFit ?? "cover";

  // Auto-detect background for transparent images when not explicitly provided
  const resolvedSolidBg =
    solidBackgroundColor !== undefined
      ? solidBackgroundColor
      : getImageThumbnailBackgroundColor(image);
  const resolvedIncludeBgBlurHash = resolvedSolidBg
    ? false
    : includeBgBlurHash;

  // Stop throwing cloudinary errors when an image isn't passed into the component
  // For example, when an operator or event doesn't have any images
  if (!image) {
    return (
      <ImageNotFound
        Icon={MediaImage}
        imageStyleProps={imageStyleProps}
        alt={t("core:IMAGE.NOT_UPLOADED")}
        errorBoundaryProps={errorBoundaryProps}
        parts={parts}
      />
    );
  }

  return (
    <PostHogErrorBoundary
      fallback={
        <ImageNotFound
          Icon={MediaImage}
          imageStyleProps={imageStyleProps}
          alt={t("core:IMAGE.NOT_UPLOADED")}
          errorBoundaryProps={errorBoundaryProps}
          parts={parts}
        />
      }
      // Do NOT send this error to PostHog
      additionalProperties={getPostHogErrorCaptureData({
        fileName: "CloudinaryImage.tsx",
        extra: {
          doNotSendToPostHog: true,
          imageId: image?._id ?? "No image ID",
          errorBoundaryMessage:
            "CloudinaryImage error boundary ... <ImageNotFound /> displayed",
        },
      })}
    >
      <CloudinaryImageInner
        image={image}
        imageStyleProps={imageStyleProps}
        resize={resize}
        resizeVideo={resizeVideo}
        previewVideo={previewVideo}
        alt={alt}
        blurhash={blurhash}
        errorBoundaryProps={errorBoundaryProps}
        includeBgBlurHash={resolvedIncludeBgBlurHash}
        solidBackgroundColor={resolvedSolidBg}
        style={style}
        parts={parts}
        videoSpecificProps={videoSpecificProps}
      />
    </PostHogErrorBoundary>
  );
};
